import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ui } from "@patient-watch/ui";
import { Loader2 } from "lucide-react";
import { FirstNameZ, LastNameZ, TitleZ } from "@patient-watch/types";
import useUserUpdate from "@/features/profile/api/useUserMutation";
import { useAuth } from "@/utils/auth";
import { formComponents as fc } from "@/features/i18n/formComponents";
import { selectProfileByIdQueryOptions } from "@/features/profile/api/selectProfile";
import type { ProfileT } from "@patient-watch/types/database";
import ProfileWrap from "@/features/profile/layouts/ProfileWrap";
import { useProfileCopy } from "@/features/i18n/profileCopy";

const formSchema = z.object({
    title: TitleZ,
    first_name: FirstNameZ,
    last_name: LastNameZ,
});

type FormSchemaType = z.infer<typeof formSchema>;

const ClinicianName = () => {
    const auth = useAuth();
    const navigate = useNavigate();
    const { profile } = Route.useLoaderData();
    const userMutation = useUserUpdate(auth.id);
    const copy = useProfileCopy();

    const form = useForm<FormSchemaType>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            title: profile?.title ?? "Dr", // Default to Dr for clinicians
            first_name: profile?.first_name ?? "",
            last_name: profile?.last_name ?? "",
        },
    });

    const onSubmit = async (formData: FormSchemaType) => {
        try {
            await userMutation.mutateAsync({
                title: formData.title,
                first_name: formData.first_name,
                last_name: formData.last_name,
            });
            void navigate({ to: "/onboarding/clinician/organisation" });
        } catch {
            /* useUserMutation shows toast on error */
        }
    };

    return (
        <ProfileWrap title={copy.onboardingNameTitle}>
            <ui.Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    <fc.SelectTitle autoFocus={true} label={copy.titleLabel} options={copy.titleOptions} />
                    <fc.FirstName name="first_name" />
                    <fc.LastName name="last_name" />

                    <ui.Button
                        type="submit"
                        className="w-full"
                        size="lg"
                        disabled={userMutation.isPending}
                        data-testid="continue-button"
                    >
                        {userMutation.isPending && <Loader2 className="mr-2 w-4 h-4 animate-spin" />}
                        {userMutation.isPending ? copy.saving : copy.continue}
                    </ui.Button>
                </form>
            </ui.Form>
        </ProfileWrap>
    );
};

export const Route = createFileRoute("/onboarding/clinician/name")({
    component: ClinicianName,
    loader: async ({ context }) => {
        let profile: ProfileT | null = null;
        if (context?.auth?.id) {
            profile = await context?.queryClient.ensureQueryData(selectProfileByIdQueryOptions(context.auth.id));
        }
        return { profile };
    },
});
